// REBUILD FINAL - Dec 27 2025 23:22 - Force new hash generation after cache clear
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import ScrollToTop from "@/components/ScrollToTop";
const Footer = lazy(() => import("@/components/Footer"));
const ChatGate = lazy(() => import("@/components/ChatGate"));
const ChatInvitePopup = lazy(() => import("@/components/ChatInvitePopup"));
const AnalyticsWrapper = lazy(() => import("@/components/AnalyticsWrapper"));
const PageViewTracker = lazy(() => import("@/components/PageViewTracker"));
const GeoRedirect = lazy(() => import("@/components/GeoRedirect"));
import ErrorBoundary from "@/components/ErrorBoundary";
import LoadingScreen from "@/components/LoadingScreen";
import GlobalStructuredData from "@/components/GlobalStructuredData";
import { DestinationCanonicalRoutes } from "./routing/DestinationCanonicalRoutes";
import { DESTINATION_ROUTES } from "@/config/destinationRoutes";
import { isEnHostname, isProductionSiteHostname } from "@/config/domains";

// Critical pages - loaded immediately (homepage only for fastest FCP/LCP)
import Index from "./pages/Index";

// High-priority pages - lazy loaded but preloaded on hover
const Contato = lazy(() => import("./pages/Contato"));
const Parceiros = lazy(() => import("./pages/Parceiros"));
const VistoD7 = lazy(() => import("./pages/VistoD7"));
const VisaD7 = lazy(() => import("./pages/VisaD7"));
const VistoD8 = lazy(() => import("./pages/VistoD8"));
const GoldenVisaEN = lazy(() => import("./pages/GoldenVisaEN"));
const GoldenVisaPT = lazy(() => import("./pages/GoldenVisaPT"));
const TodosOsVistos = lazy(() => import("./pages/TodosOsVistos"));
const AllVisas = lazy(() => import("./pages/AllVisas"));
const Imobiliario = lazy(() => import("./pages/Imobiliario"));
const Financiamento = lazy(() => import("./pages/Financiamento"));
const ConsultoriaLegal = lazy(() => import("./pages/ConsultoriaLegal"));

// Lazy loaded pages - code splitting for better performance
const Blog = lazy(() => import("./pages/Blog"));
const BlogDestinationHub = lazy(() => import("./pages/BlogDestinationHub"));
// Lazy loaded articles (blog posts)
const LicencaResidencia = lazy(() => import("./pages/articles/LicencaResidencia"));
const ComprarCasaArticle = lazy(() => import("./pages/articles/ComprarCasa"));
const MercadoLisboa = lazy(() => import("./pages/articles/MercadoLisboa"));
const InvestirImoveis = lazy(() => import("./pages/articles/InvestirImoveis"));
const TendenciasMercado = lazy(() => import("./pages/articles/TendenciasMercado"));
const CustoVida = lazy(() => import("./pages/articles/CustoVida"));
const RoteiroMudanca = lazy(() => import("./pages/articles/RoteiroMudanca"));
const SaudeEducacao = lazy(() => import("./pages/articles/SaudeEducacao"));
const NomadaDigital = lazy(() => import("./pages/articles/NomadaDigital"));
const D7vsD8 = lazy(() => import("./pages/articles/D7vsD8"));
const GoldenVisa2026 = lazy(() => import("./pages/articles/GoldenVisa2026"));
const D7Income2026 = lazy(() => import("./pages/articles/D7Income2026"));
const Aposentadoria = lazy(() => import("./pages/articles/Aposentadoria"));
const NhrFiscal = lazy(() => import("./pages/articles/NhrFiscal"));
const VistoD4CapitalHumano = lazy(() => import("./pages/articles/VistoD4CapitalHumano"));
const ReunificacaoFamiliarPortugal = lazy(() => import("./pages/articles/ReunificacaoFamiliarPortugal"));
const PropertyConsultationPortugal = lazy(() => import("./pages/articles/PropertyConsultationPortugal"));
const HouseBuyingServicePortugal = lazy(() => import("./pages/articles/HouseBuyingServicePortugal"));
const VistoD8NomadaDigitalElite = lazy(() => import("./pages/articles/VistoD8NomadaDigitalElite"));
const HealthcarePortugalPeaceOfMind = lazy(() => import("./pages/articles/HealthcarePortugalPeaceOfMind"));
const StartupVisaPortugal2026 = lazy(() => import("./pages/articles/StartupVisaPortugal2026"));
const GoldenVisaRequirements2026 = lazy(() => import("./pages/articles/GoldenVisaRequirements2026"));
const EscolherDestinoEuropaPathways = lazy(() => import("./pages/articles/EscolherDestinoEuropaPathways"));
const GoldenVisaVsD7Decision2026 = lazy(() => import("./pages/articles/GoldenVisaVsD7Decision2026"));
const RetirementVisaPortugalTimeline2026 = lazy(() => import("./pages/articles/RetirementVisaPortugalTimeline2026"));
const PortugalD7Eligibility2026 = lazy(() => import("./pages/articles/PortugalD7Eligibility2026"));
const PortugalRetirementVisaAmericans2026 = lazy(() => import("./pages/articles/PortugalRetirementVisaAmericans2026"));
const RetiringToPortugalFromIreland = lazy(() => import("./pages/articles/RetiringToPortugalFromIreland"));
const SpainResidencyPathways2026 = lazy(() => import("./pages/articles/SpainResidencyPathways2026"));
const ItalyResidencyPathways2026 = lazy(() => import("./pages/articles/ItalyResidencyPathways2026"));
const GreeceResidencyPathways2026 = lazy(() => import("./pages/articles/GreeceResidencyPathways2026"));
const D7VisaRefusalReasons2026 = lazy(() => import("./pages/articles/D7VisaRefusalReasons2026"));

// Lazy loaded secondary pages
const Negocios = lazy(() => import("./pages/Negocios"));
const GuiasEmigrantes = lazy(() => import("./pages/GuiasEmigrantes"));
const Interactive = lazy(() => import("./pages/Interactive"));
const Pathways = lazy(() => import("./pages/Pathways"));
const StartupVisa = lazy(() => import("./pages/StartupVisa"));
const StartupVisaEN = lazy(() => import("./pages/StartupVisaEN"));
const GoldenVisaEligibility = lazy(() => import("./pages/GoldenVisaEligibility"));
const SobreNos = lazy(() => import("./pages/SobreNos"));

// Lazy loaded guides
const Documentacao = lazy(() => import("./pages/guides/Documentacao"));
const Financas = lazy(() => import("./pages/guides/Financas"));
const Habitacao = lazy(() => import("./pages/guides/Habitacao"));
const Investimento = lazy(() => import("./pages/guides/Investimento"));
const RetirementGuide = lazy(() => import("./pages/guides/RetirementGuide"));
const InvestmentGuide = lazy(() => import("./pages/guides/InvestmentGuide"));
const NomadGuide = lazy(() => import("./pages/guides/NomadGuide"));
const BusinessGuide = lazy(() => import("./pages/guides/BusinessGuide"));
const RetirementPortoGuide = lazy(() => import("./pages/guides/RetirementPortoGuide"));
const InvestmentLisbonGuide = lazy(() => import("./pages/guides/InvestmentLisbonGuide"));

// Lazy loaded healthcare pages
const Saude = lazy(() => import("./pages/Saude"));
const PublicVsPrivate = lazy(() => import("./pages/healthcare/PublicVsPrivate"));
const HealthInsurance = lazy(() => import("./pages/healthcare/HealthInsurance"));
const PrivateHospitals = lazy(() => import("./pages/healthcare/PrivateHospitals"));
const VisaHealthcare = lazy(() => import("./pages/healthcare/VisaHealthcare"));
const DentalCare = lazy(() => import("./pages/healthcare/DentalCare"));
const ElderlyCare = lazy(() => import("./pages/healthcare/ElderlyCare"));
const EmergencyServices = lazy(() => import("./pages/healthcare/EmergencyServices"));
const SnsRegistration = lazy(() => import("./pages/healthcare/SnsRegistration"));

// Lazy loaded visa pages (less popular)
const VistoD1 = lazy(() => import("./pages/VistoD1"));
const VisaD1 = lazy(() => import("./pages/VisaD1"));
const VistoD2 = lazy(() => import("./pages/VistoD2"));
const VisaD2 = lazy(() => import("./pages/VisaD2"));
const VistoD3 = lazy(() => import("./pages/VistoD3"));
const VisaD3 = lazy(() => import("./pages/VisaD3"));
const VistoD4 = lazy(() => import("./pages/VistoD4"));
const VisaD4 = lazy(() => import("./pages/VisaD4"));
const VistoD5 = lazy(() => import("./pages/VistoD5"));
const VisaD5 = lazy(() => import("./pages/VisaD5"));
const VistoD6 = lazy(() => import("./pages/VistoD6"));
const VisaD6 = lazy(() => import("./pages/VisaD6"));
const Incentivos = lazy(() => import("./pages/Incentivos"));

// Lazy loaded legal/institutional pages
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const GDPRPolicy = lazy(() => import("./pages/GDPRPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Force new build hash - version bump
const BUILD_VERSION = '2.1.0';

const queryClient = new QueryClient();

function syncStaticHeroHeadlineTag(isHome: boolean) {
  const hero = document.getElementById("static-hero");
  if (!hero) return;
  const headline = hero.querySelector<HTMLElement>("[data-static-hero-headline]");
  if (!headline) return;

  const replaceHeadline = (nextTag: "h1" | "p") => {
    if (headline.tagName.toLowerCase() === nextTag) return;
    const next = document.createElement(nextTag);
    next.dataset.staticHeroHeadline = "";
    next.className = headline.className;
    const inlineStyle = headline.getAttribute("style");
    if (inlineStyle) next.setAttribute("style", inlineStyle);
    next.textContent = headline.textContent;
    headline.replaceWith(next);
  };

  if (isHome) {
    replaceHeadline("h1");
  } else {
    replaceHeadline("p");
  }
}

export function HeroBackgroundController() {
  const { pathname } = useLocation();
  useEffect(() => {
    const el = document.getElementById("static-hero");
    const isHome = pathname === "/";
    if (el) {
      el.style.display = isHome ? "" : "none";
      el.setAttribute("aria-hidden", isHome ? "false" : "true");
    }
    syncStaticHeroHeadlineTag(isHome);
  }, [pathname]);
  return null;
}

/** Legacy /comprar-casa follows the host: EN canonical on .com, PT canonical on .pt. */
function LegacyComprarCasaRedirect() {
  const to =
    typeof window !== "undefined" &&
    isProductionSiteHostname(window.location.hostname) &&
    isEnHostname(window.location.hostname)
      ? DESTINATION_ROUTES.portugal.buyProperty.en
      : DESTINATION_ROUTES.portugal.buyProperty.pt;
  return <Navigate to={to} replace />;
}

export function AppRouteElements() {
  return (
    <>
            <Route path="/" element={<Index />} />
            <Route path="/sobre-nos" element={<Suspense fallback={<LoadingScreen />}><SobreNos /></Suspense>} />
            <Route path="/about-us" element={<Suspense fallback={<LoadingScreen />}><SobreNos /></Suspense>} />
            <Route path="/blog" element={<Suspense fallback={<LoadingScreen />}><Blog /></Suspense>} />
            <Route path="/blog/licenca-residencia" element={<Suspense fallback={<LoadingScreen />}><LicencaResidencia /></Suspense>} />
            <Route path="/blog/comprar-casa" element={<Suspense fallback={<LoadingScreen />}><ComprarCasaArticle /></Suspense>} />
            <Route path="/blog/mercado-lisboa" element={<Suspense fallback={<LoadingScreen />}><MercadoLisboa /></Suspense>} />
            <Route path="/blog/investir-imoveis" element={<Suspense fallback={<LoadingScreen />}><InvestirImoveis /></Suspense>} />
            <Route path="/blog/tendencias-mercado" element={<Suspense fallback={<LoadingScreen />}><TendenciasMercado /></Suspense>} />
            <Route path="/blog/custo-vida" element={<Suspense fallback={<LoadingScreen />}><CustoVida /></Suspense>} />
            <Route path="/blog/roteiro-mudanca" element={<Suspense fallback={<LoadingScreen />}><RoteiroMudanca /></Suspense>} />
            <Route path="/blog/saude-educacao" element={<Suspense fallback={<LoadingScreen />}><SaudeEducacao /></Suspense>} />
            <Route path="/blog/nomada-digital" element={<Suspense fallback={<LoadingScreen />}><NomadaDigital /></Suspense>} />
            <Route path="/blog/aposentadoria" element={<Suspense fallback={<LoadingScreen />}><Aposentadoria /></Suspense>} />
            <Route path="/blog/nhr-fiscal" element={<Suspense fallback={<LoadingScreen />}><NhrFiscal /></Suspense>} />
            <Route path="/blog/d7-vs-d8-visa" element={<Suspense fallback={<LoadingScreen />}><D7vsD8 /></Suspense>} />
            <Route path="/blog/visto-d4-capital-humano" element={<Suspense fallback={<LoadingScreen />}><VistoD4CapitalHumano /></Suspense>} />
            <Route path="/blog/reunificacao-familiar-portugal" element={<Suspense fallback={<LoadingScreen />}><ReunificacaoFamiliarPortugal /></Suspense>} />
            <Route path="/blog/move-to-portugal-property-consultation" element={<Suspense fallback={<LoadingScreen />}><PropertyConsultationPortugal /></Suspense>} />
            <Route path="/blog/house-buying-service-portugal" element={<Suspense fallback={<LoadingScreen />}><HouseBuyingServicePortugal /></Suspense>} />
            <Route path="/blog/visto-d8-nomada-digital-elite" element={<Suspense fallback={<LoadingScreen />}><VistoD8NomadaDigitalElite /></Suspense>} />
            <Route path="/blog/golden-visa-2026-still-exists" element={<Suspense fallback={<LoadingScreen />}><GoldenVisa2026 /></Suspense>} />
            <Route path="/blog/d7-income-requirements-2026" element={<Suspense fallback={<LoadingScreen />}><D7Income2026 /></Suspense>} />
            <Route path="/blog/healthcare-portugal-peace-of-mind" element={<Suspense fallback={<LoadingScreen />}><HealthcarePortugalPeaceOfMind /></Suspense>} />
            <Route path="/blog/startup-visa-portugal-2026" element={<Suspense fallback={<LoadingScreen />}><StartupVisaPortugal2026 /></Suspense>} />
            <Route path="/blog/escolher-destino-europa-pathways" element={<Suspense fallback={<LoadingScreen />}><EscolherDestinoEuropaPathways /></Suspense>} />
            <Route path="/blog/golden-visa-requirements-portugal-2026" element={<Suspense fallback={<LoadingScreen />}><GoldenVisaRequirements2026 /></Suspense>} />
            <Route path="/blog/golden-visa-vs-d7-portugal-2026" element={<Suspense fallback={<LoadingScreen />}><GoldenVisaVsD7Decision2026 /></Suspense>} />
            <Route path="/blog/retirement-visa-portugal-timeline-2026" element={<Suspense fallback={<LoadingScreen />}><RetirementVisaPortugalTimeline2026 /></Suspense>} />
            <Route path="/blog/portugal-d7-visa-eligibility-2026" element={<Suspense fallback={<LoadingScreen />}><PortugalD7Eligibility2026 /></Suspense>} />
            <Route path="/blog/portugal-retirement-visa-americans-2026" element={<Suspense fallback={<LoadingScreen />}><PortugalRetirementVisaAmericans2026 /></Suspense>} />
            <Route path="/blog/retiring-to-portugal-from-ireland" element={<Suspense fallback={<LoadingScreen />}><RetiringToPortugalFromIreland /></Suspense>} />
            <Route path="/blog/spain-residency-pathways-2026" element={<Suspense fallback={<LoadingScreen />}><SpainResidencyPathways2026 /></Suspense>} />
            <Route path="/blog/italy-residency-pathways-2026" element={<Suspense fallback={<LoadingScreen />}><ItalyResidencyPathways2026 /></Suspense>} />
            <Route path="/blog/greece-residency-pathways-2026" element={<Suspense fallback={<LoadingScreen />}><GreeceResidencyPathways2026 /></Suspense>} />
            <Route path="/blog/d7-visa-refusal-reasons-2026" element={<Suspense fallback={<LoadingScreen />}><D7VisaRefusalReasons2026 /></Suspense>} />
            <Route path="/blog/:hubSlug" element={<Suspense fallback={<LoadingScreen />}><BlogDestinationHub /></Suspense>} />
            <Route path="/imobiliario" element={<Imobiliario />} />
            <Route path="/comprar-casa" element={<LegacyComprarCasaRedirect />} />
            <Route path="/negocios" element={<Suspense fallback={<LoadingScreen />}><Negocios /></Suspense>} />
            <Route path="/guias-emigrantes" element={<Suspense fallback={<LoadingScreen />}><GuiasEmigrantes /></Suspense>} />
            <Route path="/pathways" element={<Suspense fallback={<LoadingScreen />}><Pathways /></Suspense>} />
            <Route path="/interactive" element={<Suspense fallback={<LoadingScreen />}><Interactive /></Suspense>} />
            <Route path="/golden-visa" element={<GoldenVisaEN />} />
            <Route path="/visto-gold" element={<GoldenVisaPT />} />
            <Route path="/startup-visa-portugal" element={<Suspense fallback={<LoadingScreen />}><StartupVisa /></Suspense>} />
            <Route path="/startup-visa-portugal-en" element={<Suspense fallback={<LoadingScreen />}><StartupVisaEN /></Suspense>} />
            <Route path="/golden-visa-eligibility" element={<Suspense fallback={<LoadingScreen />}><GoldenVisaEligibility /></Suspense>} />
            <Route path="/guias-emigrantes/documentacao" element={<Suspense fallback={<LoadingScreen />}><Documentacao /></Suspense>} />
            <Route path="/guias-emigrantes/financas" element={<Suspense fallback={<LoadingScreen />}><Financas /></Suspense>} />
            <Route path="/guias-emigrantes/habitacao" element={<Suspense fallback={<LoadingScreen />}><Habitacao /></Suspense>} />
            <Route path="/saude" element={<Suspense fallback={<LoadingScreen />}><Saude /></Suspense>} />
            <Route path="/saude/publico-vs-privado" element={<Suspense fallback={<LoadingScreen />}><PublicVsPrivate /></Suspense>} />
            <Route path="/saude/seguros-saude" element={<Suspense fallback={<LoadingScreen />}><HealthInsurance /></Suspense>} />
            <Route path="/saude/hospitais-privados" element={<Suspense fallback={<LoadingScreen />}><PrivateHospitals /></Suspense>} />
            <Route path="/saude/vistos-saude" element={<Suspense fallback={<LoadingScreen />}><VisaHealthcare /></Suspense>} />
            <Route path="/saude/cuidados-dentarios" element={<Suspense fallback={<LoadingScreen />}><DentalCare /></Suspense>} />
            <Route path="/saude/cuidados-idosos" element={<Suspense fallback={<LoadingScreen />}><ElderlyCare /></Suspense>} />
            <Route path="/healthcare/public-vs-private" element={<Suspense fallback={<LoadingScreen />}><PublicVsPrivate /></Suspense>} />
            <Route path="/healthcare/health-insurance" element={<Suspense fallback={<LoadingScreen />}><HealthInsurance /></Suspense>} />
            <Route path="/healthcare/private-hospitals" element={<Suspense fallback={<LoadingScreen />}><PrivateHospitals /></Suspense>} />
            <Route path="/healthcare/visa-healthcare" element={<Suspense fallback={<LoadingScreen />}><VisaHealthcare /></Suspense>} />
            <Route path="/healthcare/dental-care" element={<Suspense fallback={<LoadingScreen />}><DentalCare /></Suspense>} />
            <Route path="/healthcare/elderly-care" element={<Suspense fallback={<LoadingScreen />}><ElderlyCare /></Suspense>} />
            <Route path="/saude/servicos-emergencia" element={<Suspense fallback={<LoadingScreen />}><EmergencyServices /></Suspense>} />
            <Route path="/healthcare/emergency-services" element={<Suspense fallback={<LoadingScreen />}><EmergencyServices /></Suspense>} />
            <Route path="/saude/inscricao-sns" element={<Suspense fallback={<LoadingScreen />}><SnsRegistration /></Suspense>} />
            <Route path="/healthcare/sns-registration" element={<Suspense fallback={<LoadingScreen />}><SnsRegistration /></Suspense>} />
            
            <Route path="/guias-emigrantes/investimento" element={<Suspense fallback={<LoadingScreen />}><Investimento /></Suspense>} />
          <Route path="/visto-d1" element={<Suspense fallback={<LoadingScreen />}><VistoD1 /></Suspense>} />
          <Route path="/visa-d1" element={<Suspense fallback={<LoadingScreen />}><VisaD1 /></Suspense>} />
          <Route path="/visto-d2" element={<Suspense fallback={<LoadingScreen />}><VistoD2 /></Suspense>} />
          <Route path="/visa-d2" element={<Suspense fallback={<LoadingScreen />}><VisaD2 /></Suspense>} />
          <Route path="/visto-d3" element={<Suspense fallback={<LoadingScreen />}><VistoD3 /></Suspense>} />
          <Route path="/visa-d3" element={<Suspense fallback={<LoadingScreen />}><VisaD3 /></Suspense>} />
          <Route path="/visto-d4" element={<Suspense fallback={<LoadingScreen />}><VistoD4 /></Suspense>} />
          <Route path="/visa-d4" element={<Suspense fallback={<LoadingScreen />}><VisaD4 /></Suspense>} />
          <Route path="/visto-d5" element={<Suspense fallback={<LoadingScreen />}><VistoD5 /></Suspense>} />
          <Route path="/visa-d5" element={<Suspense fallback={<LoadingScreen />}><VisaD5 /></Suspense>} />
          <Route path="/visto-d6" element={<Suspense fallback={<LoadingScreen />}><VistoD6 /></Suspense>} />
          <Route path="/visa-d6" element={<Suspense fallback={<LoadingScreen />}><VisaD6 /></Suspense>} />
          <Route path="/incentivos" element={<Suspense fallback={<LoadingScreen />}><Incentivos /></Suspense>} />
          <Route path="/visto-d7" element={<VistoD7 />} />
          <Route path="/visa-d7" element={<VisaD7 />} />
          <Route path="/visto-d8" element={<VistoD8 />} />
          <Route path="/visa-d8" element={<VistoD8 />} />
            <Route path="/retirement-guide/:region" element={<Suspense fallback={<LoadingScreen />}><RetirementGuide /></Suspense>} />
          <Route path="/investment-guide/:region" element={<Suspense fallback={<LoadingScreen />}><InvestmentGuide /></Suspense>} />
          <Route path="/nomad-guide/:region" element={<Suspense fallback={<LoadingScreen />}><NomadGuide /></Suspense>} />
          <Route path="/business-guide/:region" element={<Suspense fallback={<LoadingScreen />}><BusinessGuide /></Suspense>} />
            <Route path="/contato" element={<Contato />} />
            <Route path="/parceiros" element={<Parceiros />} />
            <Route path="/financiamento" element={<Financiamento />} />
            <Route path="/financing" element={<Financiamento />} />
            <Route path="/consultoria-legal" element={<ConsultoriaLegal />} />
            <Route path="/legal-consulting" element={<ConsultoriaLegal />} />
            <Route path="/todos-os-vistos" element={<TodosOsVistos />} />
            <Route path="/all-visas" element={<AllVisas />} />
            <Route path="/business" element={<Suspense fallback={<LoadingScreen />}><Negocios /></Suspense>} />
            <Route path="/contact" element={<Contato />} />
          <Route path="/politica-privacidade" element={<Suspense fallback={<LoadingScreen />}><PrivacyPolicy /></Suspense>} />
          <Route path="/privacy-policy" element={<Suspense fallback={<LoadingScreen />}><PrivacyPolicy /></Suspense>} />
          <Route path="/termos-uso" element={<Suspense fallback={<LoadingScreen />}><TermsOfUse /></Suspense>} />
          <Route path="/terms-of-use" element={<Suspense fallback={<LoadingScreen />}><TermsOfUse /></Suspense>} />
          <Route path="/rgpd" element={<Suspense fallback={<LoadingScreen />}><GDPRPolicy /></Suspense>} />
          <Route path="/gdpr" element={<Suspense fallback={<LoadingScreen />}><GDPRPolicy /></Suspense>} />
            {DestinationCanonicalRoutes()}
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<Suspense fallback={<LoadingScreen />}><NotFound /></Suspense>} />
    </>
  );
}

const App = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);

  return (
    <BrowserRouter basename="/">
      <HeroBackgroundController />
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <LanguageProvider>
            <GlobalStructuredData />
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Suspense fallback={null}>
                <AnalyticsWrapper />
                <GeoRedirect />
                <PageViewTracker />
              </Suspense>
                <>
                  <ScrollToTop />
            <div className="min-h-screen w-full max-w-full overflow-x-hidden">
              {/* Skip to main content link for accessibility */}
              <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg"
                onClick={(e) => {
                  e.preventDefault();
                  const main = document.getElementById('main-content');
                  main?.focus();
                  main?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Skip to main content
              </a>
              <Header />
              <main id="main-content" className="pt-20 lg:pt-24" tabIndex={-1}>
                <Suspense fallback={<LoadingScreen />}>
                <Routes>
                  {AppRouteElements()}
                </Routes>

                </Suspense>
              </main>
              <Suspense fallback={<footer className="min-h-[280px] bg-muted/20" aria-hidden="true" />}>
                <Footer />
              </Suspense>
            </div>
            <Suspense fallback={null}>
              <ChatInvitePopup onOpenChat={() => setIsChatOpen(true)} />
              <ChatGate isOpen={isChatOpen} onToggle={() => setIsChatOpen(!isChatOpen)} />
            </Suspense>
                </>
            </TooltipProvider>
          </LanguageProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default App;
